import { MuiTelInput } from 'mui-tel-input';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import CaloLoader from 'src/components/CaloLoader';
import Icon from 'src/components/Icon';
import { LoginMethod } from 'src/lib/enum';
import { caloTheme } from 'src/theme/calo';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import LoadingCircle from '../../components/LoadingCircle';
import ResendButton from '../../components/ResendButton';
import { countryCodes } from '../../lib/countries';
import { useCountryDetection } from '../../lib/countryDetection';
import { removeAllWhiteSpacesAndDashes } from '../../lib/utils';
import useSignInForm from './useSignInForm';

const isEmailValid = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const SignInForm = () => {
  const { detectedCountry, isDetecting, isActuallyDetected } = useCountryDetection();
  const location = useLocation();

  const isUKFromPath = location.pathname === '/uk';

  const isUKUser = (detectedCountry === 'GB' && isActuallyDetected) || isUKFromPath;

  const [loginMethod, setLoginMethod] = useState<LoginMethod>(
    isUKUser ? LoginMethod.EMAIL : LoginMethod.PHONE
  );

  const [phoneUpdateCompleted, setPhoneUpdateCompleted] = useState(false);

  useEffect(() => {
    if (!isDetecting && !isUKUser && loginMethod === LoginMethod.EMAIL) {
      setLoginMethod(LoginMethod.PHONE);
    }
  }, [isDetecting, isUKUser, loginMethod]);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    submitForm,
    isValid,
    isSubmitting,
    loading,
    handleResend,
    handleEditPhone,
    isEditingPhone,
    resendIn,
    retryCountdown,
    setFieldValue
  } = useSignInForm(loginMethod);

  useEffect(() => {
    if (errors.username === 'Please log in using your phone number') {
      setPhoneUpdateCompleted(true);
      if (loginMethod === LoginMethod.EMAIL) {
        setLoginMethod(LoginMethod.PHONE);
        setFieldValue('username', '');
        setFieldValue('code', '');
        setFieldValue('codeSent', false);
      }
    }
  }, [errors.username, loginMethod, setFieldValue]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: LoginMethod) => {
    if (phoneUpdateCompleted && newValue === LoginMethod.EMAIL) {
      return;
    }

    setLoginMethod(newValue);
    setFieldValue('username', '');
    setFieldValue('code', '');
    setFieldValue('codeSent', false);
  };

  const validateField = (value: string) => {
    if (loginMethod === LoginMethod.EMAIL) {
      return isEmailValid(value);
    }
    return true;
  };

  return (
    <Container component="main" maxWidth="xs">
      {isDetecting ? <CaloLoader /> : (
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Icon name="calo" width={320} height={200} />
          <Typography component="h1" variant="h4">
            Sign in
          </Typography>

          {isUKUser && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <Tabs
                value={loginMethod}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 500
                  }
                }}
              >
                <Tab label="Phone" value="phone" />
                <Tab
                  label="Email"
                  value="email"
                  disabled={phoneUpdateCompleted}
                  sx={{
                    opacity: phoneUpdateCompleted ? 0.5 : 1,
                    cursor: phoneUpdateCompleted ? 'not-allowed' : 'pointer'
                  }}
                />
              </Tabs>
              {phoneUpdateCompleted && (
                <Typography
                  variant="caption"
                  sx={{
                    mt: 1,
                    display: 'block',
                    textAlign: 'center',
                    color: 'text.secondary',
                    fontStyle: 'italic'
                  }}
                >
                  Please use your phone number to sign in
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ mt: 2, width: '100%' }}>
            {loginMethod === 'phone' ? (
              <MuiTelInput
                margin="normal"
                required
                fullWidth
                id="phoneNumber"
                label="Phone Number"
                name="phoneNumber"
                value={values.username}
                onChange={(e) => {
                  handleChange('username')(removeAllWhiteSpacesAndDashes(e));
                }}
                error={Boolean(errors.username && touched.username)}
                helperText={<>{errors.username}</>}
                disabled={values.codeSent && !isEditingPhone}
                autoFocus
                onlyCountries={countryCodes}
                defaultCountry={detectedCountry}
              />
            ) : (
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                type="email"
                value={values.username}
                onChange={handleChange('username')}
                onBlur={handleBlur('username')}
                error={Boolean(errors.username && touched.username)}
                helperText={errors.username && touched.username ? errors.username : ''}
                disabled={values.codeSent && !isEditingPhone}
                autoFocus
              />
            )}

            {values.codeSent && !isEditingPhone && loginMethod === LoginMethod.PHONE && (
              <Button
                onClick={() => handleEditPhone(values.username)}
                variant="text"
                size="small"
                sx={{ mt: 1, textTransform: 'none' }}
              >
                Edit phone number
              </Button>
            )}

            {values.codeSent && !isEditingPhone && loginMethod === LoginMethod.EMAIL && (
              <Button
                onClick={() => {
                  handleEditPhone(values.username);
                  setLoginMethod(LoginMethod.EMAIL);
                }}
                variant="text"
                size="small"
                sx={{ mt: 1, textTransform: 'none' }}
              >
                Edit email address
              </Button>
            )}

            {values.codeSent && (
              <TextField
                margin="normal"
                required
                fullWidth
                name="otpCode"
                label="OTP Code"
                id="otpCode"
                placeholder="123456"
                error={Boolean(errors.code && touched.code)}
                helperText={<>{errors.code}</>}
                value={values.code}
                onChange={handleChange('code')}
                onBlur={handleBlur('code')}
              />
            )}

            <Button
              onClick={submitForm}
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, color: caloTheme.palette.white }}
              disabled={
                loading ||
                !isValid ||
                isSubmitting ||
                (values.codeSent && values.code.length !== 6) ||
                Boolean(retryCountdown) ||
                !validateField(values.username)
              }
            >
              {retryCountdown
                ? `Try again in ${retryCountdown}`
                : values.codeSent
                  ? 'Verify'
                  : 'Send code'}
            </Button>

            {values.codeSent && (
              <ResendButton
                handleResend={() => handleResend(values.username)}
                resendIn={resendIn}
              />
            )}
          </Box>
          {loading && <LoadingCircle />}
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Typography
          sx={{ opacity: '25%', position: 'fixed', fontSize: '10px', bottom: 0, textAlign: 'center', marginBottom: 1 }}
        >
          This site is protected by reCAPTCHA and the Google{' '}
          <a href="https://policies.google.com/privacy">Privacy Policy</a> and{' '}
          <a href="https://policies.google.com/terms">Terms of Service</a> apply.
        </Typography>
      </Box>
    </Container>
  );
};

export default SignInForm;
